import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import HeroSection from "../components/HeroSection";
import TestimonialCard from "../components/TestimonialCard";
import SlideInWhenVisible from "../components/SlideInWhenVisible";
import SEO from "../components/SEO";
import tourApi from "../api/tour";
import reviewApi from "../api/review";
import { useSettings } from "../context/SettingsContext";

// Animated counter hook
function useCountUp(target, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!startOnView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, startOnView]);

  return { count, ref };
}

// Skeleton loader component
function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 animate-pulse">
      <div className="h-48 bg-slate-200 dark:bg-slate-800" />
      <div className="p-4 space-y-3">
        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
      </div>
    </div>
  );
}

function ReviewSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-full bg-slate-200 dark:bg-slate-700" />
        <div className="space-y-2 flex-1">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/4" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-5/6" />
      </div>
    </div>
  );
}

// Stat card with animated counter
function StatCard({ icon, target, fixedValue, suffix, label }) {
  const { count, ref } = useCountUp(target || 0, 1500);
  return (
    <div
      ref={ref}
      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 text-center shadow-sm transition-all hover:shadow-md hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 transition-transform group-hover:scale-110">
        {icon}
      </div>
      <p className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
        {fixedValue || count}{suffix}
      </p>
      <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">{label}</p>
    </div>
  );
}

// Section header component
function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="mb-8 flex items-end justify-between">
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        )}
        <div className="mt-2 h-1 w-12 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
      </div>
      {action}
    </div>
  );
}

export default function HomePage() {
  const [tours, setTours] = useState([]);
  const [allToursCount, setAllToursCount] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loadingTours, setLoadingTours] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [toursError, setToursError] = useState(null);
  const [reviewsError, setReviewsError] = useState(null);
  const { settings } = useSettings();
  const cs = settings.currency_symbol || "$";
  const discount = Number(settings.discount_percentage) || 0;

  useEffect(() => {
    (async () => {
      setLoadingTours(true);
      try {
        const { data } = await tourApi.listTours();
        const today = new Date().toISOString().split("T")[0];
        const availableTours = data.filter((tour) => {
          if (!tour.start_date) return true;
          const tourDate = tour.start_date.split("T")[0];
          return tourDate >= today;
        });
        setAllToursCount(availableTours.length);
        const top3Tours = availableTours
          .sort((a, b) => b.rating - a.rating)
          .slice(0, 3);
        setTours(top3Tours);
      } catch (err) {
        setToursError("Failed to load tours");
      } finally {
        setLoadingTours(false);
      }
    })();

    (async () => {
      setLoadingReviews(true);
      try {
        const { data } = await reviewApi.listReviews();
        const top3Reviews = data.sort((a, b) => b.rating - a.rating).slice(0, 3);
        setReviews(top3Reviews);
      } catch (err) {
        setReviewsError("Failed to load reviews");
      } finally {
        setLoadingReviews(false);
      }
    })();
  }, []);

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length)
    : 4.8;

  return (
    <div className="space-y-16">
      <SEO
        title="Desert Tours, Sahara Trips & Morocco Adventures"
        description="Explore Morocco with Moroccan Desert Trek. Discover Sahara desert tours, camel trekking, Marrakech excursions, Atlas Mountains hikes, and authentic Moroccan adventures. Book your unforgettable trip today!"
        path="/"
      />
      <SlideInWhenVisible direction="right" delay={0.2} duration={0.8}>
        <HeroSection />
      </SlideInWhenVisible>

      {/* Stats Section */}
      <section>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <SlideInWhenVisible direction="up" delay={0}>
            <StatCard
              icon={<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              target={allToursCount || 30}
              suffix="+"
              label="Available Tours"
            />
          </SlideInWhenVisible>
          <SlideInWhenVisible direction="up" delay={0.1}>
            <StatCard
              icon={<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
              target={500}
              suffix="+"
              label="Happy Travelers"
            />
          </SlideInWhenVisible>
          <SlideInWhenVisible direction="up" delay={0.2}>
            <StatCard
              icon={<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>}
              fixedValue={avgRating.toFixed(1)}
              suffix=""
              label="Average Rating"
            />
          </SlideInWhenVisible>
          <SlideInWhenVisible direction="up" delay={0.3}>
            <StatCard
              icon={<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              target={98}
              suffix="%"
              label="Satisfaction Rate"
            />
          </SlideInWhenVisible>
        </div>
      </section>

      {/* Top Tours */}
      <section>
        <SectionHeader
          title="Top-Rated Tours"
          subtitle="Our most popular experiences, handpicked for you"
          action={
            <Link to="/tours" className="text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors">
              View All Tours &rarr;
            </Link>
          }
        />
        {loadingTours ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => <CardSkeleton key={i} />)}
          </div>
        ) : toursError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-500 dark:text-red-400">{toursError}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {tours.map((tour, index) => {
              const discountedPrice = discount > 0
                ? (tour.price * (1 - discount / 100)).toFixed(2)
                : null;
              return (
                <SlideInWhenVisible key={tour.id} direction="up" delay={index * 0.15}>
                  <Link
                    to={`/tours/${tour.id}`}
                    className="block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={tour.thumbnail || "/emogiesnack.png"}
                        alt={tour.title}
                        loading="lazy"
                        className="h-48 w-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {/* Rating badge */}
                      <div className="absolute top-3 right-3">
                        <span className="rounded-full bg-white/90 backdrop-blur-sm px-2.5 py-1 text-xs font-semibold text-amber-600 shadow-sm dark:bg-slate-900/90 dark:text-amber-400">
                          {tour.rating}★
                        </span>
                      </div>
                      {/* Discount badge */}
                      {discount > 0 && (
                        <div className="absolute top-3 left-3">
                          <span className="rounded-full bg-red-500 px-2.5 py-1 text-xs font-bold text-white shadow-sm">
                            -{discount}%
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 space-y-2">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                        {tour.title}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {tour.city} · {tour.duration_days} days
                      </p>
                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2">
                          {discountedPrice ? (
                            <>
                              <span className="text-sm line-through text-slate-400">{cs}{tour.price}</span>
                              <span className="text-lg font-bold text-emerald-600">{cs}{discountedPrice}</span>
                            </>
                          ) : (
                            <span className="text-lg font-bold text-emerald-600">{cs}{tour.price}</span>
                          )}
                          <span className="text-xs text-slate-400">/ person</span>
                        </div>
                        <span className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white group-hover:bg-emerald-600 transition-colors dark:bg-slate-700 dark:group-hover:bg-emerald-500">
                          Details
                        </span>
                      </div>
                    </div>
                  </Link>
                </SlideInWhenVisible>
              );
            })}
          </div>
        )}
      </section>

      {/* Why Choose Us */}
      <section>
        <SectionHeader
          title="Why Choose Us"
          subtitle="What makes our tours special"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: (<svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>),
              title: "Expert Local Guides",
              desc: "Born and raised in Morocco, our guides share authentic stories and hidden gems you won't find in any guidebook.",
              color: "emerald",
            },
            {
              icon: (<svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>),
              title: "Safe & Reliable",
              desc: "Your safety is our priority. Fully insured tours with 24/7 support throughout your entire journey.",
              color: "blue",
            },
            {
              icon: (<svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>),
              title: "Best Price Guarantee",
              desc: "All-inclusive pricing with no hidden fees. If you find a better deal elsewhere, we'll match it.",
              color: "amber",
            },
            {
              icon: (<svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>),
              title: "Tailored Experiences",
              desc: "Every tour is customizable. Tell us your preferences and we'll craft the perfect Moroccan adventure.",
              color: "rose",
            },
          ].map((feature, i) => {
            const colors = {
              emerald: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 group-hover:bg-emerald-600 group-hover:text-white dark:group-hover:bg-emerald-500",
              blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 group-hover:bg-blue-600 group-hover:text-white dark:group-hover:bg-blue-500",
              amber: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 group-hover:bg-amber-600 group-hover:text-white dark:group-hover:bg-amber-500",
              rose: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 group-hover:bg-rose-600 group-hover:text-white dark:group-hover:bg-rose-500",
            };
            return (
              <SlideInWhenVisible key={feature.title} direction="up" delay={i * 0.1}>
                <div className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900">
                  <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl transition-colors ${colors[feature.color]}`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </SlideInWhenVisible>
            );
          })}
        </div>
      </section>

      {/* Top Reviews */}
      <section>
        <SectionHeader
          title="Traveler Reviews"
          subtitle="What our travelers say about their experience"
          action={
            <Link to="/tours" className="text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors">
              See All Reviews &rarr;
            </Link>
          }
        />
        {loadingReviews ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => <ReviewSkeleton key={i} />)}
          </div>
        ) : reviewsError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-500 dark:text-red-400">{reviewsError}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {reviews.map((r, index) => (
              <SlideInWhenVisible
                key={r.id}
                direction="up"
                delay={index * 0.15}
              >
                <TestimonialCard
                  name={r.user?.name || "Anonymous"}
                  text={r.comment}
                  rating={r.rating}
                  tourName={r.tour?.title}
                  createdAt={r.created_at}
                />
              </SlideInWhenVisible>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <SlideInWhenVisible direction="up" delay={0.2}>
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 p-8 md:p-12 text-white shadow-lg">
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white" />
            <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white" />
            <div className="absolute top-1/2 left-1/2 h-24 w-24 rounded-full bg-white" />
          </div>
          <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">Ready for Your Moroccan Adventure?</h3>
              <p className="text-emerald-100 max-w-lg">
                Book your dream tour today. Expert guides, unforgettable landscapes, and memories that last a lifetime.
              </p>
            </div>
            <Link
              to="/tours"
              className="rounded-lg bg-white px-6 py-3 font-semibold text-emerald-700 shadow-lg hover:bg-emerald-50 hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap"
            >
              Explore Tours
            </Link>
          </div>
        </section>
      </SlideInWhenVisible>
    </div>
  );
}
